import { useCallback, useMemo, useState } from 'react';

import { useOutletContext } from 'react-router-dom';

import usePermissions from 'api/usePermissions';
import useReports from 'api/useReports';
import { useUserDetails } from 'api/useUserDetails';
import { ELocalStorageKeys } from 'constants/enums';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getSelectProjectOptions } from 'utils/projectsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import EmptyState from 'components/EmptyState/EmptyState';
import { TableView } from 'components/TableView';

import InfoBanner from '../Components/InfoBanner';
import { FINANCIAL_REPORTS_TABLE_COLUMNS } from '../constants/financialReports';
import { FinancialReportsOutletContext } from '../types/financialReports.types';
import { transformReportsToTableData } from '../utils';

const FinancialReportsTable = () => {
    const { selectedProjects, startDate, endDate } = useOutletContext<FinancialReportsOutletContext>();
    const { getPermissions } = usePermissions();
    const { userDetails } = useUserDetails();
    const projectsDetails = userDetails?.projects;
    const permissions = getPermissions();
    const allowedPublishers = permissionsUtil.getPublishersByFinancialReports(permissions);

    const [showInfoBanner, setShowInfoBanner] = useState(() => {
        const savedState = localStorageUtil.get(
            ELocalStorageKeys.FINANCIAL_REPORTS_INFO_BANNER_CLOSED
        );
        return savedState === null;
    });

    const { getReportDownload, getReports } = useReports(
        projectsDetails
            ? getSelectProjectOptions(projectsDetails, allowedPublishers)[0].value
            : undefined
    );

    const onDownloadClick = useCallback((reportId: string) => {
        getReportDownload(reportId);
        showToast({
            message: 'Report download started',
            status: EToasterStatus.SUCCESS
        });
    }, [getReportDownload]);

    const handleCloseInfoBanner = () => {
        setShowInfoBanner(false);
        localStorageUtil.set(ELocalStorageKeys.FINANCIAL_REPORTS_INFO_BANNER_CLOSED, 'true');
    };

    const columns = useMemo(() => {
        return FINANCIAL_REPORTS_TABLE_COLUMNS(onDownloadClick);
    }, [onDownloadClick]);

    const tableData = useMemo(() => {
        const allReports = transformReportsToTableData(
            getReports.data,
            projectsDetails,
            allowedPublishers
        );

        let filtered = allReports;

        // Apply project filter
        if (selectedProjects.length > 0) {
            filtered = filtered.filter((report) => {
                return selectedProjects.some((selectedProjectId) => {
                    return report.publishers.includes(selectedProjectId);
                });
            });
        }

        // Apply date range filter - strict range (both start and end must be within filter range)
        if (startDate && endDate) {
            const filterStartDate = dayjs(startDate).startOf('day');
            const filterEndDate = dayjs(endDate).endOf('day');

            filtered = filtered.filter((report) => {
                const reportStartDate = dayjs(report.startDate);
                const reportEndDate = dayjs(report.endDate);

                return !reportStartDate.isBefore(filterStartDate) && !reportEndDate.isAfter(filterEndDate);
            });
        }

        return filtered;
    }, [getReports.data, projectsDetails, allowedPublishers, selectedProjects, startDate, endDate]);

    const isEmptyState = !getReports.isLoading && isEmpty(tableData);

    return (
        <>
            {!isEmpty(getReports.data) && showInfoBanner && (<InfoBanner onClose={handleCloseInfoBanner} />)}
            {isEmptyState ? (
                <div className="flex items-center justify-center w-full min-h-[calc(100vh-250px)]">
                    <EmptyState
                        title="No reports yet"
                        description={
                            <>
                                Your reports will appear here.
                                <br />
                                Generate a new report to begin.
                            </>
                        }
                        icon="fa-regular fa-file-chart-column"
                        hideButton={true}
                    />
                </div>
            ) : (
                <TableView
                    title="Reports List"
                    data={tableData}
                    columns={columns}
                    isLoading={getReports.isLoading}
                />
            )}
        </>
    );
};

export default FinancialReportsTable;

